// Conexión SSE ÚNICA para todos los canales de la aplicación.
//
// Problema que resuelve: en HTTP/1.1 el navegador limita ~6 conexiones por origen y cada canal
// SSE ocupaba una permanentemente. Con 8 canales el pool se agotaba: dejaban de conectar los
// streams nuevos y, si compartían origen con la página, ni siquiera se podía recargar. Ahora el
// servidor manda todos los canales por un solo stream (/sse/events) etiquetando cada evento con
// su 'canal', y aquí se reparte a quien lo haya pedido.
//
// Encima va SseCompartido (patrón pestaña-líder), así que la conexión es una por NAVEGADOR, no
// por pestaña: con 3 pestañas abiertas sigue habiendo 1 sola conexión.
//
// Para el servidor también es mejor: un SseEmitter y un contexto asíncrono por usuario en vez
// de ocho, independientemente de la versión de HTTP.

import { EventSourcePolyfill } from 'event-source-polyfill';
import ItemClasificacion from './ItemClasificacion.js';
import { baseSse } from './sseBase.js';
import SseCompartido from './SseCompartido.js';

// canal -> [callback]
var suscriptores = {};
var compartida = null;
// Último payload por canal: quien se suscribe tarde recupera el estado sin esperar al siguiente
// evento (el servidor hace lo mismo al conectar; esto cubre a los que llegan con la conexión ya abierta).
var ultimoPorCanal = {};

function readToken() {
    try {
        var session = JSON.parse(localStorage.getItem('session') || '{}');
        return session.access_token || session.token || session.accessToken || '';
    } catch (e) {
        return '';
    }
}

function url() {
    return baseSse(ItemClasificacion.server_movilidad) + ItemClasificacion.prefix + 'sse/events';
}

/** Entrega un sobre {canal, payload} a los suscriptores de ese canal. */
function repartir(sobre) {
    if (!sobre || !sobre.canal) return;
    ultimoPorCanal[sobre.canal] = sobre.payload;
    var lista = suscriptores[sobre.canal] || [];
    lista.slice().forEach(function (cb) {
        try { cb(sobre.payload); } catch (err) { console.error('[SseHub] suscriptor de ' + sobre.canal + ' falló', err); }
    });
}

function asegurarConexion() {
    if (compartida) return;
    compartida = SseCompartido.conectar('hub', {
        // Solo la ejecuta la pestaña líder. Se reconecta sola: si el stream queda CERRADO (el
        // servidor se reinicia, el token caduca, la red parpadea) el polyfill NO vuelve por su
        // cuenta, y como la líder sigue reteniendo el Web Lock ninguna seguidora toma el relevo:
        // TODAS las pestañas se quedan mudas hasta cerrarlas. Reabrir aquí es lo único que lo evita.
        abrir: function (emitir) {
            var es = null;
            var cerrado = false;
            var reintento = null;
            var intentos = 0;

            function abrirStream() {
                es = new EventSourcePolyfill(url(), {
                    headers: { Authorization: 'Bearer ' + readToken() },
                    heartbeatTimeout: 1800000
                });
                es.addEventListener('open', function () {
                    intentos = 0;
                });
                es.addEventListener('hub', function (event) {
                    var sobre = null;
                    try { sobre = JSON.parse(event.data); } catch (err) { return; }
                    emitir(sobre);
                });
                es.addEventListener('error', function (err) {
                    console.warn('[SseHub] error de conexión', err);
                    // readyState 2 = CLOSED: el polyfill se ha rendido y hay que reabrir a mano.
                    // Con 0 (CONNECTING) está reintentando él solo y no hay que estorbarle.
                    if (!cerrado && es && es.readyState === 2) {
                        reconectar();
                    }
                });
            }

            function reconectar() {
                if (cerrado || reintento) return;
                try { es.close(); } catch (e) {}
                es = null;
                intentos += 1;
                var espera = Math.min(30000, 1000 * Math.pow(2, intentos - 1));
                console.warn('[SseHub] reconectando en ' + espera + ' ms (intento ' + intentos + ')');
                reintento = setTimeout(function () {
                    reintento = null;
                    if (!cerrado) abrirStream();
                }, espera);
            }

            abrirStream();

            return {
                close: function () {
                    cerrado = true;
                    if (reintento) { clearTimeout(reintento); reintento = null; }
                    if (es) { try { es.close(); } catch (e) {} es = null; }
                }
            };
        },
        onEvento: repartir
    });
}

export default {
    /**
     * Escucha un canal. Devuelve el último evento conocido de ese canal si lo hay, para que quien
     * llega tarde a un trabajo en curso no se quede en blanco.
     */
    subscribe(canal, callback) {
        if (typeof callback !== 'function') return;
        if (!suscriptores[canal]) suscriptores[canal] = [];
        if (suscriptores[canal].indexOf(callback) < 0) suscriptores[canal].push(callback);
        asegurarConexion();
        if (ultimoPorCanal[canal] !== undefined) {
            // Asíncrono: el llamante aún está terminando de suscribirse.
            setTimeout(function () { try { callback(ultimoPorCanal[canal]); } catch (e) {} }, 0);
        }
    },
    unsubscribe(canal, callback) {
        var lista = suscriptores[canal];
        if (!lista) return;
        var i = lista.indexOf(callback);
        if (i >= 0) lista.splice(i, 1);
        // La conexión NO se cierra al quedarse un canal sin oyentes: es única y compartida por
        // todos, y cerrarla obligaría a reabrirla constantemente al navegar entre pantallas.
    },
    /** Cierra la conexión (logout). */
    close() {
        if (compartida) { try { compartida.desconectar(); } catch (e) {} compartida = null; }
        ultimoPorCanal = {};
    },
    ultimoDe(canal) { return ultimoPorCanal[canal]; }
};
